// Tiny DOM helpers. Text always goes in via textContent, so player names can't inject HTML.

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { className?: string } = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const e = Object.assign(document.createElement(tag), props);
  for (const c of children) {
    if (c) e.append(c);
  }
  return e;
}

/**
 * Puts a window `panel` on the page; Escape or a press outside it closes it, as does the
 * returned function (its close button). `closed` runs when it has closed.
 */
export function openWindow(panel: HTMLElement, closed = () => {}): () => void {
  const close = () => {
    panel.remove();
    closed();
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('pointerdown', onPress, true);
  };
  const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
  const onPress = (e: PointerEvent) => !panel.contains(e.target as Node) && close();
  window.addEventListener('keydown', onKey);
  // Pressing, not clicking: the click that opened the window is over by now.
  window.addEventListener('pointerdown', onPress, true);
  document.body.append(panel);
  return close;
}

/**
 * Asks in a window in the middle, in the game's style (instead of the browser's `confirm`):
 * true for `yes`, false for `no`, Escape or a press outside.
 */
export function ask(question: string, yes: string, no: string): Promise<boolean> {
  return new Promise((answer) => {
    let said = false;
    const say = (value: boolean) => {
      said = true;
      close();
      answer(value);
    };
    const panel = el('div', { id: 'ask', className: 'panel' });
    const close = openWindow(panel, () => said || answer(false));
    const stay = el('button', { className: 'big', textContent: no, onclick: () => say(false) });
    panel.replaceChildren(
      el('p', { textContent: question }),
      el('div', { className: 'row' }, el('button', { className: 'primary big', textContent: yes, onclick: () => say(true) }), stay),
    );
    stay.focus();
  });
}

/** Coloured dot for a tank. */
export function dot(color: string): HTMLSpanElement {
  const d = el('span', { textContent: '● ' });
  d.style.color = color;
  return d;
}

/** Sets only the properties of `target` that differ, so an unchanged frame costs no DOM work. */
export function patch<T extends object>(target: T, props: Partial<T>): void {
  for (const k in props) {
    if (target[k] !== props[k]) target[k] = props[k] as T[typeof k];
  }
}
